import { Button, Input, Modal, Select, Space, DatePicker, message, Tabs, Upload, Row, Col, Spin, Form, Pagination, Checkbox } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { orderColumns } from './constants';
import "./dispatch.scss";
import {
  getUserDetails,
  getDefaultCenterFilter,
  getVisibleCenterOptions,
  makePostCall,
} from '../../utils/helper';
import { useAllowedModalities } from '../../hooks/useAllowedModalities';
import { useSites } from '../../hooks/useSites';
import SeriesSelectionModal from '../../components/SeriesSelectionModal';

import dayjs from 'dayjs';
import FyzksInput from '../../components/FyzksInput';
import AgGridTable from '../../components/AgGridTable';
import { debounce } from 'lodash';
import ReportViewer from '../ReportViewer';
import ViewNotes from '../ManageNodes/ViewNotes';
import { useForm } from 'antd/es/form/Form';
import { launchViewerOnDualMonitor, checkMonitorSetup, openSeriesViewer } from '../../utils/MonitorManager';
import { FieldLabels, getPacsStatusLabel } from '../../constants/fieldLabels';
import { openReportWindow, showPdfInWindow } from '../../utils/tableActionHandlers';
import {sortedModalityOptionsNew} from '../../utils/helper';
import { maxTagTooltip } from '../../utils/selectHelpers';
import PatientOrdersModal, { PatientOrdersModalRef } from '../../components/PatientOrdersModal';

const { RangePicker } = DatePicker;

const DispatchList = ({ isConsultant, StyledTabsComponent }) => {
  const [orders, setOrders] = useState({ data: [], loading: true, total: 0 });
  const [filters, setFilters] = useState(() => ({ center: getDefaultCenterFilter() }));
  // const [savedFilters, setSavedFilters] = useState([]);
  const userDetails = getUserDetails();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const [viewreportModal, setViewReportModal] = useState({ visible: false, pdfData: null });
  const [receiverModal, setReceiverModal] = useState({ visible: false, details: null });
  const [viewNotesModal, setViewNotesModal] = useState<{ visible: boolean; details: any } | null>({ visible: false, details: null });
  const [noteEditActive, setNoteEditActive] = useState(false);
  const [saveNoteHandler, setSaveNoteHandler] = useState(null);
  const [selectedNote, setSelectedNote] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  const [receiverForm] = useForm();
  const patientOrdersModalRef = useRef<PatientOrdersModalRef>(null);

  // Series-selection modal (shared component). Opens the study's series list; picking
  // series launches the viewer filtered to them. Gated by study-viewing permission.
  const [seriesSelectionModal, setSeriesSelectionModal] = useState<{ open: boolean; studyUID: string | null; record: any }>(
    { open: false, studyUID: null, record: null }
  );

  const handleSelectSeries = (record: any) => {
    const studyUid = record?.pacs_study?.ps_study_uid ?? record?.ps_study_uid ?? record?.po_study_uid;
    if (!studyUid) {
      message.warning('No study images found for this order');
      return;
    }
    setSeriesSelectionModal({ open: true, studyUID: studyUid, record });
  };

  const closeSeriesModal = () => setSeriesSelectionModal(prev => ({ ...prev, open: false }));

  const handleOpenSeries = async (studyUID: string, selectedSeries: string[], record: any) => {
    openSeriesViewer(studyUID, selectedSeries, record);
  };

  const today = dayjs();
  const twoWeeksAgo = dayjs().subtract(14, 'days');

  // Set the default value to [twoWeeksAgo, today]
  const [dateRange, setDateRange] = useState([twoWeeksAgo, today]);
  const [printLoading, setPrintLoading] = useState(false);
  // Modality multi-select (matches radiologist/technician pages)
  const [modalityAllSelected, setModalityAllSelected] = useState(true);
  const { modalityOptions } = useAllowedModalities();
  const sortedModalityOptions = sortedModalityOptionsNew(modalityOptions);
  // Site/Group multi-select — same source and same visibility rules as the PACS list
  // (technicians only ever see their own assigned center).
  const { centers: allCenters } = useSites();
  const centers = useMemo(() => getVisibleCenterOptions(allCenters), [allCenters]);

  // Radiologists + HODs for the "Reported By" multi-select filter (mirrors the PACS list).
  const [userList, setUserList] = useState([]);
  const [reportedByOpen, setReportedByOpen] = useState(false);
  const radHodOptions = useMemo(() =>
    (userList || [])
      .filter((user: any) => ['radiologist', 'hod'].includes(user.user_type))
      .map((user: any) => ({ label: `${user.user_fullname} (${user.username})`, value: user.username })),
    [userList]);

  useEffect(() => {
    makePostCall('/user-list', {})
      .then(res => setUserList(res.data?.data || []))
      .catch(() => setUserList([]));
  }, []);

  const viewNotes = (rec) => {
    setSelectedNote(rec?.note_id || {});
    setViewNotesModal({ visible: true, details: { pacs_order: rec } });
    setNoteEditActive(false);
  };

  const handleNoteSelection = useCallback((note, _record) => {
    setSelectedNote(prevSelectedNote => {
      const previousNoteId = (prevSelectedNote as any)?.rn_id || null;
      const nextNoteId = note?.rn_id || null;
      const previousNoteText = (prevSelectedNote as any)?.rn_notes || '';
      const nextNoteText = note?.rn_notes || '';

      if (previousNoteId === nextNoteId && previousNoteText === nextNoteText) {
        return prevSelectedNote;
      }

      return note || {};
    });
  }, []);

  const openReport = async record => {
    if (isConsultant) {
      // window.open(`/viewer?StudyInstanceUIDs=${record?.po_study_uid}`, '_blank');
      await launchViewerOnDualMonitor(record?.po_study_uid, record);
    }
    // printReport(record)
    captureReceiver(record);
  };

  // Open the study images in the viewer (dedicated eye-icon action / prior-orders popup).
  const openViewer = async record => {
    // The dispatch/consultant list is order-centric; the study UID comes from the
    // joined pacs_study (falls back to any legacy top-level field). Prior-order
    // rows from PatientOrdersModal pass ps_study_uid at the top level.
    const studyUid = record?.pacs_study?.ps_study_uid ?? record?.ps_study_uid ?? record?.po_study_uid;
    if (!studyUid) {
      message.warning('No study images found for this order');
      return;
    }
    const viewerWindow = await launchViewerOnDualMonitor(studyUid, record);
    if (!viewerWindow) message.warning('Could not open viewer window');
  };

  // Consultant: clicking the SIGNEDOFF status chip opens the signed-off report in
  // the browser's native PDF viewer (separate popup window) — mirrors the PACS
  // table's status-chip behaviour. Uses the same /print-acc-report source as the
  // dispatch print flow. The window is opened synchronously (before the await) so
  // it isn't treated as a popup and blocked.
  const viewReport = async record => {
    const orderId = record?.pacs_ord_id ?? record?.pacs_order?.pacs_ord_id;
    if (!orderId) {
      message.error('No report found for this study');
      return;
    }
    const reportWindow = openReportWindow();
    setPrintLoading(true);
    try {
      const res = await makePostCall(
        '/print-acc-report',
        {
          order_id: orderId,
          user_id: userDetails?.username,
          fromDispatch: false,
        },
        { responseType: 'arraybuffer' }
      );
      showPdfInWindow(reportWindow, res.data);
    } catch (err) {
      console.log('Error', err);
      message.error('Failed to view report');
      if (reportWindow && !reportWindow.closed) reportWindow.close();
    } finally {
      setPrintLoading(false);
    }
  };

  // Consultant: prior-orders popup for the row's patient (same modal the PACS
  // list uses). Opens read-only actions — signed-off report and image viewer.
  const handleShowPatientOrders = (record: any) => {
    patientOrdersModalRef.current?.open(record);
  };

  const filterResults = (tempFilters, page = 1, pageSize = 20, overrideDateRange = undefined) => {
    setOrders({ ...orders, loading: true, data: [] });
    const payload = {
      ...tempFilters,
      page: page - 1, // Backend expects 0-indexed page
      limit: pageSize,
    };

    const effectiveDateRange = overrideDateRange !== undefined ? overrideDateRange : dateRange;
    if (effectiveDateRange) {
      payload['from_date'] = dayjs(effectiveDateRange[0]).format('YYYYMMDD');
      payload['to_date'] = dayjs(effectiveDateRange[1]).format('YYYYMMDD');
      payload['po_study_date'] = effectiveDateRange;
    }

    makePostCall('/dispatch-list', payload)
      .then(res => {
        setOrders({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0,
        });
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false, total: 0 });
      });
  };

  const handleFilterChange = (key, value) => {
    const temp_filters = { ...filters };
    temp_filters[key] = value;
    setFilters(temp_filters);
  };

  const clearFilters = () => {
    const defaults = { center: getDefaultCenterFilter() };
    setFilters(defaults);
    setDateRange(null);
    setSelectedRowKeys([]);
    setModalityAllSelected(true);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    filterResults(defaults, 1, pagination.pageSize, null);
  };

  useEffect(() => {
    console.log('Filters changed', filters);
    // Reset to page 1 when filters change
    if (Object.keys(filters).length > 0 || dateRange) {
      setPagination({ current: 1, pageSize: pagination.pageSize });
    }
    debouncedFilter(filters);
  }, [filters]);

  useEffect(() => {
    console.log('date changed', filters);
    // if (Object.keys(filters).length) {
    debouncedFilter(filters);
    // }
  }, [dateRange]);

  const debouncedFilter = useCallback(
    debounce(tempFilters => {
      console.log('Debounced value:');
      // When filters change via debounce, use page 1 (already set in useEffect)
      filterResults(tempFilters, 1, pagination.pageSize);
    }, 500),
    [dateRange, pagination.pageSize]
  );

  const statusOptions =
    userDetails?.user_type === 'doc'
      ? [{ label: 'SIGNEDOFF', value: 'SIGNEDOFF' }]
      : [
          { label: 'NEW', value: 'PENDING' },
          { label: getPacsStatusLabel('SCANNED'), value: 'SCANNED' },
          { label: 'DRAFTED', value: 'DRAFTED' },
          { label: 'REVIEWED', value: 'REVIEWED' },
          { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
        ];

  const bodyPartOptions = [
    { label: 'Head', value: 'HEAD' },
    { label: 'Brain', value: 'BRAIN' },
    { label: 'Abdomen', value: 'ABDOMEN' },
    { label: 'Chest', value: 'CHEST' },
    { label: 'Leg', value: 'LEG' },
    { label: 'Hand', value: 'HAND' },
  ];

  const siteOptions = [
    { label: 'Somajiguda', value: 'SOMAJIGUDA' },
    { label: 'HiTech City', value: 'HITEC CITY' },
    { label: 'Secunderabad', value: 'SECUNDERABAD' },
  ];

  const captureReceiver = rec => {
    setReceiverModal({ visible: true, details: rec });
  };

  const captureAndPrint = () => {
    const receiverInfo = receiverForm.getFieldsValue();
    printReport(receiverModal?.details, receiverInfo);
  };

  const printReport = async (rec, receiverInfo) => {
    // A dispatch-list row IS the order (pacs_ord_id at top level); older callers
    // may pass a nested { pacs_order } shape, so fall back to that.
    const orderId = rec?.pacs_ord_id ?? rec?.pacs_order?.pacs_ord_id;
    const { receiver_mobile, receiver_name } = receiverInfo || {};
    setReceiverModal({ visible: false });
    setPrintLoading(true);
    makePostCall(
      '/print-acc-report',
      {
        order_id: orderId,
        user_id: userDetails?.username,
        fromDispatch: !isConsultant || userDetails?.user_type === 'dispatch',
        received_by: receiverInfo ? `${receiver_name} | ${receiver_mobile}` : null,
      },
      {
        responseType: 'arraybuffer',
      }
    )
      .then(async res => {
        const pdfBlob = new Blob([res.data], { type: 'application/pdf' });

        const pdf_url = URL.createObjectURL(pdfBlob);

        setPrintLoading(false);
        const buffer = await pdfBlob.arrayBuffer();

        if (isConsultant) {
          setViewReportModal({ visible: true, pdfData: buffer, pdfBlob: pdfBlob, pdf_url });
        } else {
          // setPdfUrl(url);
          const printWindow = window.open(pdf_url, '_blank');
          printWindow.print();
        }
      })
      .catch(err => {
        console.log('Error', err);
        setPrintLoading(false);
      });
  };

  return (
    <Spin spinning={printLoading}>
      <div>
        {StyledTabsComponent && StyledTabsComponent()}
        <div style={{ padding: '0.75rem 1.25rem 0' }}>
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: '0.75rem',
              border: '1px solid #E2E8F1',
              padding: '1rem 1.25rem',
              marginBottom: '1rem',
            }}
          >
            {/* Action buttons sit on their own top row (right-aligned), mirroring
                the PACS list toolbar placement. */}
            <div className="filter-toolbar">
              <div className="filter-toolbar-main" />
              <div className="filter-toolbar-actions">
                <button
                  className="btn-primary btn-compact"
                  onClick={() => {
                    setPagination({ current: 1, pageSize: pagination.pageSize });
                    filterResults(filters, 1, pagination.pageSize);
                  }}
                >
                  Search
                </button>
                <button className="btn-secondary btn-compact" onClick={() => { clearFilters(); }}>
                  Clear Search
                </button>
              </div>
            </div>
            {/* Filters: 70% inputs | 30% modality */}
            <div className="filter-layout">
              <div className="disp-filters-row filter-layout-inputs">
                <div className="disp-field disp-field--name">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.PATIENT_NAME}
                    value={filters['pat_name'] || ''}
                    onChange={e => handleFilterChange('pat_name', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--id">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.PATIENT_ID_SHORT}
                    value={filters['po_pin'] || ''}
                    onChange={e => handleFilterChange('po_pin', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--acc">
                  <FyzksInput
                    size="small"
                    placeholder="Acc. No"
                    value={filters['po_acc_no'] || ''}
                    onChange={e => handleFilterChange('po_acc_no', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--ordno">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.ORDER_NUMBER}
                    value={filters['his_ord_no'] || ''}
                    onChange={e => handleFilterChange('his_ord_no', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--visit">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.VISIT_ID}
                    value={filters['po_visit_number'] || ''}
                    onChange={e => handleFilterChange('po_visit_number', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--ref">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.REFERRING_DOCTOR}
                    value={filters['po_ref_doc'] || ''}
                    onChange={e => handleFilterChange('po_ref_doc', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--reportedby">
                  <Select
                    size="small"
                    showSearch
                    mode="multiple"
                    autoClearSearchValue={false}
                    allowClear
                    maxTagCount={1}
                    className="single-line-tags"
                    maxTagPlaceholder={maxTagTooltip}
                    placeholder={FieldLabels.REPORTED_BY}
                    value={filters['po_reported_by']}
                    style={{ width: '100%' }}
                    popupMatchSelectWidth={false}
                    dropdownStyle={{ minWidth: '16rem' }}
                    options={radHodOptions}
                    optionRender={(option) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Checkbox checked={filters['po_reported_by']?.includes(option.value)} style={{ pointerEvents: 'none' }} />
                        <span>{option.label}</span>
                      </div>
                    )}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={val => handleFilterChange('po_reported_by', val)}
                    // Clicking the field again while open closes the dropdown (same as PACS).
                    open={reportedByOpen}
                    onDropdownVisibleChange={setReportedByOpen}
                    onMouseDown={e => {
                      if ((e.target as HTMLElement).closest('.ant-select-dropdown')) return;
                      if (reportedByOpen) { e.preventDefault(); setReportedByOpen(false); }
                    }}
                  />
                </div>

                {/* <div className="disp-field disp-field--aet">
                  <FyzksInput
                    size="small"
                    placeholder={FieldLabels.AE_TITLE}
                    value={filters['ps_sending_aet'] || ''}
                    onChange={e => handleFilterChange('ps_sending_aet', e.target.value)}
                  />
                </div> */}

                <div className="disp-field disp-field--body">
                  <FyzksInput
                    size="small"
                    placeholder="Body Part / Study Desc"
                    value={filters['po_body_part'] || ''}
                    onChange={e => handleFilterChange('po_body_part', e.target.value)}
                  />
                </div>

                <div className="disp-field disp-field--center">
                  <Select
                    size="small"
                    mode="multiple"
                    allowClear
                    maxTagCount={1}
                    className="single-line-tags"
                    maxTagPlaceholder={maxTagTooltip}
                    placeholder="Site/Group"
                    value={filters['center']}
                    style={{ width: '100%' }}
                    options={centers}
                    onChange={val => handleFilterChange('center', val)}
                    popupMatchSelectWidth={false}
                    dropdownStyle={{ minWidth: '14rem' }}
                    dropdownRender={menu => (
                      <>
                        <div
                          className="select-all-dropdown-header"
                          onMouseDown={e => e.preventDefault()}
                        >
                          <Checkbox
                            checked={centers.length > 0 && (filters['center']?.length || 0) === centers.length}
                            indeterminate={
                              (filters['center']?.length || 0) > 0 &&
                              (filters['center']?.length || 0) < centers.length
                            }
                            onChange={e =>
                              handleFilterChange('center', e.target.checked ? centers.map(c => c.value) : [])
                            }
                          >
                            Select all
                          </Checkbox>
                        </div>
                        {menu}
                      </>
                    )}
                  />
                </div>

                <div className="disp-field disp-field--status">
                  <Select
                    size="small"
                    mode="multiple"
                    allowClear
                    maxTagCount={1}
                    className="single-line-tags"
                    maxTagPlaceholder={maxTagTooltip}
                    placeholder="Status"
                    style={{ width: '100%' }}
                    options={statusOptions}
                    value={filters['pacs_status']}
                    onChange={val => handleFilterChange('pacs_status', val)}
                  />
                </div>

                <div className="disp-field disp-field--date">
                  <RangePicker
                    size="small"
                    style={{ width: '100%' }}
                    placeholder={['Study Date', '']}
                    value={dateRange}
                    onChange={val => {
                      if (val) {
                        setDateRange([val[0], val[1]]);
                      } else {
                        setDateRange(null);
                      }
                    }}
                  />
                </div>

              </div>

              <div className="filter-layout-modalities">
                <div className="filter-modalities-header">
                  <Checkbox
                    className="filter-modalities-selectall"
                    checked={modalityAllSelected}
                    indeterminate={!modalityAllSelected && (filters['modality']?.length > 0)}
                    onChange={e => {
                      setModalityAllSelected(e.target.checked);
                      handleFilterChange('modality', []);
                    }}
                  >
                    Select all
                  </Checkbox>
                </div>
                <Checkbox.Group
                  value={modalityAllSelected ? sortedModalityOptions.map(o => o.value) : (filters['modality'] || [])}
                  onChange={vals => {
                    const allChecked = vals.length === sortedModalityOptions.length;
                    setModalityAllSelected(allChecked);
                    handleFilterChange('modality', allChecked ? [] : vals);
                  }}
                >
                  {sortedModalityOptions.map(opt => (
                    <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
                  ))}
                </Checkbox.Group>
              </div>
            </div>
          </div>
          <div className="orders-list">
            <AgGridTable
              height="calc(100vh - 18rem)"
              loading={orders.loading}
              rowData={orders.data || []}
              rowKey={(rec) => rec?.pacs_ord_id ?? rec?.pacs_order?.pacs_ord_id ?? rec?.po_acc_no}
              columns={orderColumns({
                openReportEditor: openReport,
                role: userDetails?.user_type,
                viewNotes,
                printReport,
                captureReceiver,
                isConsultant,
                openViewer,
                viewReport,
                selectSeries: handleSelectSeries,
                showPatientOrders: handleShowPatientOrders,
              })}
            />
            {/* External pagination for server-side paging */}
            <div className="pacs-ag-grid-pagination">
              <Pagination
                size="small"
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={orders?.total || 0}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                onChange={(page, pageSize) => {
                  setPagination({ current: page, pageSize });
                  filterResults(filters, page, pageSize);
                }}
                showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`}
              />
            </div>
          </div>
          {/* Prior orders popup (all roles) */}
          <PatientOrdersModal
            ref={patientOrdersModalRef}
            onOpenReport={viewReport}
            onOpenViewer={openViewer}
            onViewReport={viewReport}
          />
          {/* Series selection modal (shared with PACS / My Worklist) */}
          <SeriesSelectionModal
            open={seriesSelectionModal.open}
            studyUID={seriesSelectionModal.studyUID}
            record={seriesSelectionModal.record}
            onClose={closeSeriesModal}
            onOpenSeries={handleOpenSeries}
          />
          {viewreportModal && viewreportModal?.visible && (
            <Modal
              open={viewreportModal.visible}
              onCancel={() => {
                setViewReportModal({ visible: false, pdfData: null });
              }}
              width={900}
              className="viewer-modal"
            >
              <ReportViewer
                pdfData={viewreportModal?.pdfData}
                pdfBlob={viewreportModal?.pdfBlob}
                pdf_url={viewreportModal?.pdf_url}
              />
            </Modal>
          )}
            {viewNotesModal && viewNotesModal.visible && (
              <Modal
                open={viewNotesModal.visible}
                onCancel={() => {
                  if (noteEditActive) {
                    setNoteEditActive(false);
                    return;
                  }
                  setViewNotesModal(null);
                  setNoteEditActive(false);
                }}
                onOk={async () => {
                  if (saveNoteHandler) {
                    const saved = await saveNoteHandler();
                    if (!saved) {
                      return;
                    }
                  }
                }}
                style={{ width: '100%', height: '100%', top: '5%' }}
                width={'90%'}
                okButtonProps={{
                  style: { marginTop: '20px' },
                  disabled: !noteEditActive,
                }}
                okText="Save"
                cancelText={noteEditActive ? 'Cancel' : 'Close'}
              >
                <ViewNotes
                  handleNoteSelection={handleNoteSelection}
                  selectedNote={selectedNote}
                  viewNotesModal={viewNotesModal}
                  isEditMode={noteEditActive}
                  onEditNoteStateChange={setNoteEditActive}
                  onSaveNoteRegister={setSaveNoteHandler}
                />
              </Modal>
            )}
          {receiverModal && receiverModal.visible && (
            <Modal
              open={receiverModal.visible}
              onCancel={() => {
                setReceiverModal(null);
              }}
              onOk={() => {
                setReceiverModal(null);
              }}
              width={'37.5rem'}
              okButtonProps={{ style: { display: 'none' } }}
              className="receiver-modal"
            >
              <div style={{ padding: '1rem 0.5rem' }}>
                <Form
                  form={receiverForm}
                  onFinish={() => {
                    captureAndPrint();
                  }}
                >
                  <Form.Item
                    name="receiver_name"
                    label="Receiver Name"
                    rules={[
                      {
                        required: true,
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="receiver_mobile"
                    label="Receiver Mobile"
                    rules={[
                      {
                        pattern: /^[6-9]\d{9}$/,
                        message: 'Please enter a valid 10-digit mobile number!',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item className="text-center">
                    <div>
                      <Button
                        type="primary"
                        htmlType="submit"
                        style={{ width: '18.75rem' }}
                      >
                        Proceed
                      </Button>
                    </div>
                    <div>
                      <Button
                        type="link"
                        onClick={() => printReport(receiverModal?.details)}
                      >
                        Continue without Info
                      </Button>
                    </div>
                  </Form.Item>
                </Form>
              </div>
            </Modal>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default DispatchList;
